import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, onSnapshot, where, Timestamp, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useClient } from '../contexts/ClientContext';
import { AlertTriggered, AlertType } from '../types/alerts';

export interface AlertTriggeredFilters {
  alertId?: string;
  tomaId?: number;
  alertType?: AlertType;
  searchTerm?: string;
}

export const useAlertTriggered = (filters?: AlertTriggeredFilters) => {
  const [triggeredAlerts, setTriggeredAlerts] = useState<AlertTriggered[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedClient } = useClient();

  // Cargar alertas disparadas en tiempo real
  useEffect(() => {
    let isMounted = true;

    if (!selectedClient) {
      setTriggeredAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const triggeredRef = collection(db, 'alertTriggered');

    // Query base: alertas del cliente, ordenadas por fecha
    const triggeredQuery = query(
      triggeredRef,
      where('clientId', '==', selectedClient.clientId),
      limit(500) // Limitar para performance
    );

    const unsubscribe = onSnapshot(triggeredQuery, (snapshot) => {
      if (!isMounted) return;

      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AlertTriggered[];

      // Aplicar filtros adicionales en cliente
      if (filters?.alertId) {
        data = data.filter(a => a.alertId === filters.alertId);
      }
      if (filters?.tomaId !== undefined) {
        data = data.filter(a => a.tomaId === filters.tomaId);
      }
      if (filters?.alertType) {
        data = data.filter(a => a.alertType === filters.alertType);
      }
      if (filters?.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        data = data.filter(a =>
          a.alertName.toLowerCase().includes(term) ||
          a.triggeredProducts.some(p =>
            p.title.toLowerCase().includes(term) ||
            p.id_catalogo.toLowerCase().includes(term)
          )
        );
      }

      // Ordenar en cliente para evitar índice compuesto
      data.sort((a, b) => {
        const aTime = (a.triggeredAt as Timestamp | undefined)?.toMillis?.() || 0;
        const bTime = (b.triggeredAt as Timestamp | undefined)?.toMillis?.() || 0;
        return bTime - aTime;
      });

      setTriggeredAlerts(data);
      setLoading(false);
    }, (err) => {
      if (!isMounted) return;

      // Ignorar errores internos de Firestore que no afectan la funcionalidad
      if (err.message?.includes('INTERNAL ASSERTION FAILED')) {
        console.warn('Firestore internal warning (ignorable):', err.message);
        return;
      }

      console.error('Error loading triggered alerts:', err);
      setError('Error al cargar las alertas disparadas');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [selectedClient, filters?.alertId, filters?.tomaId, filters?.alertType, filters?.searchTerm]);

  // Agrupar alertas por toma
  const alertsByToma = useMemo(() => {
    const grouped: Record<number, AlertTriggered[]> = {};

    triggeredAlerts.forEach(alert => {
      if (!grouped[alert.tomaId]) {
        grouped[alert.tomaId] = [];
      }
      grouped[alert.tomaId].push(alert);
    });

    // Ordenar por número de toma descendente
    return Object.entries(grouped)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([tomaId, alerts]) => ({
        tomaId: Number(tomaId),
        tomaDate: alerts[0]?.tomaDate || '',
        alerts,
        totalTriggerCount: alerts.reduce((sum, a) => sum + a.triggerCount, 0)
      }));
  }, [triggeredAlerts]);

  // Obtener lista única de tomas con alertas
  const tomasWithAlerts = useMemo(() => {
    const tomas = new Set<number>();
    triggeredAlerts.forEach(a => tomas.add(a.tomaId));
    return Array.from(tomas).sort((a, b) => b - a);
  }, [triggeredAlerts]);

  // Obtener tipos de alerta presentes
  const presentAlertTypes = useMemo(() => {
    const types = new Set<AlertType>();
    triggeredAlerts.forEach(a => types.add(a.alertType));
    return Array.from(types);
  }, [triggeredAlerts]);

  // Verificar si un producto tiene alertas disparadas en una toma específica
  const getTriggeredAlertsForProduct = useCallback((
    productId: string,
    tomaId?: number
  ): AlertTriggered[] => {
    return triggeredAlerts.filter(alert => {
      const hasProduct = alert.triggeredProducts.some(p => p.id_catalogo === productId);
      if (tomaId !== undefined) {
        return hasProduct && alert.tomaId === tomaId;
      }
      return hasProduct;
    });
  }, [triggeredAlerts]);

  // Verificar si un producto disparó alertas recientemente (última toma)
  const hasRecentTrigger = useCallback((productId: string): boolean => {
    if (alertsByToma.length === 0) return false;

    const latestToma = alertsByToma[0];
    return latestToma.alerts.some(alert =>
      alert.triggeredProducts.some(p => p.id_catalogo === productId)
    );
  }, [alertsByToma]);

  // Estadísticas generales
  const stats = useMemo(() => {
    const totalAlerts = triggeredAlerts.length;
    const totalProducts = triggeredAlerts.reduce((sum, a) => sum + a.triggerCount, 0);
    const emailsSent = triggeredAlerts.filter(a => a.emailSent).length;

    return {
      totalAlerts,
      totalProducts,
      emailsSent,
      tomasCount: tomasWithAlerts.length
    };
  }, [triggeredAlerts, tomasWithAlerts]);

  return {
    triggeredAlerts,
    alertsByToma,
    tomasWithAlerts,
    presentAlertTypes,
    loading,
    error,
    getTriggeredAlertsForProduct,
    hasRecentTrigger,
    stats
  };
};
